import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Clock,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Sparkles,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/inverted-nipple-correction-mumbai";

const INVERTED_NIPPLE_FAQS = [
  {
    question: "Can I breastfeed after inverted nipple correction surgery?",
    answer:
      "This depends entirely on which technique is used. The duct-preserving technique maintains the milk ducts and breastfeeding function is often preserved. The non-duct preserving technique divides the ducts and breastfeeding is not possible afterward. Patients who plan to breastfeed should discuss this explicitly during consultation so the technique selection accounts for it.",
  },
  {
    question: "Will inverted nipples return after correction?",
    answer:
      "Recurrence is uncommon but possible, particularly in Grade 3 inversion where the constricting tissue is most severe. The duct-preserving technique carries a slightly higher recurrence risk than non-duct preserving correction because the release is less complete. Dr. Jain discusses expected recurrence risk relative to your specific grade during consultation.",
  },
  {
    question: "Can both nipples be corrected in the same procedure?",
    answer:
      "Yes. When inversion affects both nipples, both are corrected in a single procedure under the same local anesthesia session. There is no benefit to separating them into two procedures.",
  },
  {
    question: "Is inverted nipple correction appropriate for men?",
    answer:
      "Yes. Nipple inversion occurs in both men and women and the correction procedure is identical regardless of gender. Technique selection is based on inversion grade and anatomy, not on the patient's sex.",
  },
  {
    question: "Are results from inverted nipple correction permanent?",
    answer:
      "In most cases, yes. The released fibrous tissue does not regenerate in the same way. Recurrence, when it occurs, is typically partial rather than complete and is more common in Grade 3 cases. For the majority of patients, the correction is long-lasting.",
  },
  {
    question: "Is the procedure painful?",
    answer:
      "Local anesthesia is administered before the procedure, so there is no pain during surgery. Mild soreness and swelling at the nipple base for 2 to 3 days afterward is normal and managed with standard medication.",
  },
  {
    question: "How visible is the scar after inverted nipple correction?",
    answer:
      "Minimal. The incision is made at the base of the nipple within the natural transition between the nipple and areola. It is not visible at normal viewing distance and fades further over the months following surgery.",
  },
  {
    question: "When should I consider inverted nipple correction surgery?",
    answer:
      "When the inversion affects your confidence, comfort, hygiene, or breastfeeding function and non-surgical approaches have not produced a lasting result. There is no benefit to delaying correction if the concern is present and the patient is in good health. Earlier correction in mild grades allows the duct-preserving technique to be used more reliably before fibrous tissue becomes more established.",
  },
];

const ductContraindication = {
  "@type": "MedicalContraindication",
  name: "Future breastfeeding after non–duct-preserving inverted nipple correction",
  description:
    "When milk ducts are divided for adequate release in severe inversion, breastfeeding from the corrected breast is not preserved. This is a key consideration when choosing technique; mammogram screening protocols are unchanged, but breastfeeding goals must be reviewed in consultation before surgery.",
};

const invertedStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Inverted nipple correction",
      alternateName: ["Inverted nipple repair", "Nipple eversion surgery"],
      description:
        "Surgical release of fibrous bands and short ducts tethering the nipple inward, with outward repositioning and purse-string support; duct-preserving vs non-duct-preserving options based on inversion grade. Appropriate for male and female patients. Consultation must address breastfeeding plans and technique selection; non-duct-preserving approaches are contraindicated when preserving breastfeeding from that breast is required.",
      procedureType: "Surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Nipple" },
      howPerformed:
        "Local anesthesia; incision at nipple base; release of tethering tissue; duct-preserving or duct-dividing technique as indicated; purse-string sutures at nipple base; protective dressing; 30–60 minutes; no hospital admission.",
      url: PAGE_URL,
      contraindication: ductContraindication,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical" },
        {
          "@type": "PropertyValue",
          name: "Patient population",
          value: "Adults; male and female patients",
        },
        {
          "@type": "PropertyValue",
          name: "Consultation focus",
          value:
            "Breastfeeding goals and duct preservation vs division are discussed before finalizing the surgical plan.",
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: INVERTED_NIPPLE_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function InvertedNippleJsonLd() {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(invertedStructuredData) }} />
  );
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Breast", href: "/#services" },
    { name: "Inverted Nipple Correction", href: "/inverted-nipple-correction-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Breast</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Inverted Nipple Correction Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Grade 1, 2 &amp; 3 Inversion Correction by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Inverted Nipple Correction" icon="✨" size="hero"  imageSrc={HERO.invertedNippleCorrection} imageAlt="Inverted nipple correction"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What is Inverted Nipple Correction Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Inverted nipple correction is a minor surgical procedure that releases the internal structures pulling the nipple
              inward and repositions it to a natural outward projection. The procedure is performed under local anesthesia, takes 30
              to 60 minutes, and most patients resume normal activity within a few days. It is appropriate for both men and women.
            </p>
            <p>
              An inverted nipple occurs when the nipple is retracted inward rather than projecting outward. The cause is short or
              tight milk ducts and fibrous bands that tether the nipple to the underlying tissue. In most cases this is congenital,
              present from birth, though it can also develop after infection, surgery, or trauma. The degree of inversion determines
              which surgical technique is appropriate and whether the milk ducts can be preserved.
            </p>
            <p>
              Dr. Vicky Jain performs inverted nipple correction for patients with Grade 1, 2, or 3 inversion. Grade
              determines the treatment approach. Mild inversion responds to less invasive techniques. Severe inversion requires
              division of the restricting tissue and cannot be reliably treated non-surgically.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Inverted Nipple Correction</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The central decision in inverted nipple correction is whether the milk ducts can be preserved. Duct preservation
                maintains breastfeeding function but limits how aggressively the constricting tissue can be released, which means
                the correction is less complete and recurrence risk is slightly higher. Dividing the ducts allows a more thorough
                release and more durable outward projection but eliminates breastfeeding from that breast. Dr. Jain makes this
                recommendation based on the inversion grade and whether the patient plans to breastfeed.
              </p>
              <p>
                Grade 1 and most Grade 2 inversions are treated with duct-preserving technique. Grade 3 inversions typically require
                duct division for adequate correction. Purse-string sutures are placed at the nipple base to maintain the corrected
                position during healing. The result is an outward-projecting nipple that behaves naturally and is visible immediately
                after surgery.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function InversionGradesSection() {
  const cards = [
    {
      title: "Grade 1: Mild Inversion",
      icon: CircleDot,
      copy: "The nipple can be pulled outward manually and holds its position temporarily before retracting. Milk ducts are minimally affected. Duct-preserving surgical techniques work well at this grade. Non-surgical suction devices may provide temporary improvement but do not produce lasting correction.",
    },
    {
      title: "Grade 2: Moderate Inversion",
      icon: Minus,
      copy: "The nipple can be pulled outward but retracts quickly when released. Fibrous bands are moderately tight. Surgical correction is the reliable solution. Duct-preserving techniques are often still possible at this grade depending on anatomy.",
    },
    {
      title: "Grade 3: Severe Inversion",
      icon: ChevronDown,
      copy: "The nipple cannot be pulled outward manually. It is permanently retracted and the surrounding skin may be drawn inward. Surgical division of the constricting tissue is required. Non-duct preserving techniques are typically necessary at this grade, which means breastfeeding may not be preserved.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="inversion-grades-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Grades</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Grades of Nipple Inversion</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]" data-testid={`grade-card-${index}`}>
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const INVERTED_TECHNIQUES = [
  {
    title: "Duct-Preserving Technique",
    body:
      "The fibrous bands tethering the nipple are released while the milk ducts are carefully maintained intact. Appropriate for Grade 1 and some Grade 2 inversions. Offers the best prospect of preserving breastfeeding function after correction. The trade-off is that duct preservation limits how aggressively the constricting tissue can be released, meaning recurrence risk is slightly higher than with non-duct preserving approaches.",
  },
  {
    title: "Non-Duct Preserving Technique",
    body:
      "The milk ducts are divided to allow complete release of the inverted nipple. Used for Grade 3 inversion and cases where duct-preserving release has not produced adequate correction. Provides more reliable, durable outward projection. Breastfeeding is not possible after this technique as the milk ducts are no longer continuous.",
  },
  {
    title: "Purse-String Sutures",
    body:
      "After the nipple is released and positioned outward, purse-string sutures are placed at the nipple base to maintain the corrected projection during healing and beyond. Used as a component of most inverted nipple correction procedures rather than as a standalone technique.",
  },
  {
    title: "Non-Surgical Options",
    body:
      "Suction devices and nipple exercises are available for Grade 1 inversion. They provide temporary improvement in mild cases but do not produce lasting correction. Surgical correction is the only reliable permanent solution for any grade of inversion.",
  },
];

function TechniquesSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="techniques-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Surgical Techniques for Inverted Nipple Correction
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {INVERTED_TECHNIQUES.map((item) => (
            <div
              key={item.title}
              className="bg-white p-8 border border-[#E8E2D9]"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">How Inverted Nipple Correction is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 30 to 60 minutes under local anesthesia. No hospital admission is required. Dr. Jain makes a small
              incision at the base of the nipple. Through this incision, the fibrous bands and, in non-duct preserving cases, the
              short milk ducts responsible for the inversion are released. The nipple is repositioned outward to its corrected
              projection.
            </p>
            <p>
              Purse-string sutures are placed around the base of the nipple to hold the corrected position during healing. A
              protective dressing is applied. Both nipples can be corrected in the same procedure when both are inverted.
            </p>
            <p>
              The nipple holds its corrected outward position immediately after surgery. The surrounding area has mild swelling for
              the first few days. Final shape stabilizes at 3 to 4 weeks. Recurrence is uncommon but is more likely in severe Grade
              3 cases than in milder grades.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild swelling and minimal discomfort at the correction site. The protective dressing stays in place. Normal and expected.",
      icon: BedDouble,
    },
    {
      period: "Days 4 to 7",
      description:
        "Most patients resume normal daily activity. The dressing is removed at the post-operative visit. The nipple's outward projection is visible.",
      icon: Monitor,
    },
    {
      period: "Week 2",
      description: "Sutures dissolve or are removed. The nipple continues to settle into its corrected position.",
      icon: Clock,
    },
    {
      period: "Weeks 3 to 4",
      description:
        "Final shape stabilizes. The corrected projection is permanent. Gym and strenuous activity resume after 3 to 4 weeks.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">
              Who Is a Good Candidate for Inverted Nipple Correction?
            </h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are adults of any age, male or female, with Grade 1, 2, or 3 nipple inversion that affects their
              appearance, confidence, hygiene, or function. There is no minimum age beyond adulthood and no upper limit. Patients
              do not need to have severe inversion to benefit from correction. Grade 1 patients who find the appearance bothersome
              and want a permanent solution rather than temporary suction devices are appropriate candidates.
            </p>
            <p>
              Patients who plan to breastfeed in the future are counseled on technique selection as the central part of the surgical
              plan. If preserving breastfeeding function is a priority, the duct-preserving technique is used where the inversion
              grade allows it. Patients with Grade 3 inversion who want to breastfeed are informed directly that non-duct preserving
              technique is likely required and that breastfeeding will not be possible from the corrected side afterward. This
              conversation happens during consultation, before any surgical plan is finalized.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const NON_SURGICAL_COMPARISON = [
  {
    label: "Appropriate Grade",
    nonSurgical: "Grade 1 mild inversion only.",
    surgical: "Grade 1, 2, and 3.",
  },
  {
    label: "How It Works",
    nonSurgical: "Suction devices or exercises apply outward tension to stretch the constricting tissue gradually.",
    surgical: "Fibrous bands or milk ducts are released through a small incision, permanently freeing the nipple.",
  },
  {
    label: "Results Duration",
    nonSurgical: "Temporary. Nipple retracts when suction is removed or exercises stop.",
    surgical: "Permanent in the majority of cases.",
  },
  {
    label: "Downtime",
    nonSurgical: "None.",
    surgical: "3 to 5 days to normal activity.",
  },
  {
    label: "Recurrence",
    nonSurgical: "High. The underlying cause is not addressed.",
    surgical: "Low for Grade 1 and 2. Slightly higher for Grade 3 due to severity of constriction.",
  },
];

function NonSurgicalVsSurgicalSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="non-surgical-vs-surgical-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Non-Surgical vs. Surgical Inverted Nipple Correction
        </h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[26%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Non-Surgical
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  Surgical
                </th>
              </tr>
            </thead>
            <tbody>
              {NON_SURGICAL_COMPARISON.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.nonSurgical}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.surgical}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center leading-relaxed">
          Non-surgical options are worth trying first for Grade 1 patients who want to avoid any procedure. For Grade 2 and Grade
          3, surgery is the only approach that produces reliable, lasting correction.
        </p>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {INVERTED_NIPPLE_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`inverted-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">
          Book Your Inverted Nipple Correction Consultation
        </h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your inversion grade, discusses whether duct preservation is achievable for
          your anatomy, and explains the technique appropriate for your degree of inversion. If you plan to breastfeed in the
          future, this is discussed as a central part of the surgical planning. You leave with a clear understanding of the
          procedure, the expected result, and what recovery involves.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Breast Augmentation",
      description: "Enhance breast volume alongside nipple correction for complete breast aesthetics.",
      href: "/breast-implant-surgery-mumbai",
    },
    {
      name: "Breast Lift",
      description: "Address breast position and shape alongside nipple correction in a single procedure.",
      href: "/breast-lift-surgery-mumbai",
    },
    {
      name: "Breast Reduction",
      description: "Correct size, position, and nipple placement together for patients with macromastia.",
      href: "/breast-reduction-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function InvertedNippleCorrectionPage() {
  return (
    <>
      <SEO
        title="Inverted Nipple Correction Surgery | Dr. Vicky Jain"
        description="Nipples retracted inward affecting appearance or function. Dr. Vicky Jain corrects Grade 1, 2, and 3 nipple inversion. Book your consultation today."
        canonical="/inverted-nipple-correction-mumbai"
      />
      <InvertedNippleJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <InversionGradesSection />
      <TechniquesSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <CandidateSection />
      <NonSurgicalVsSurgicalSection />
      <FAQSection />
      <CTASection />
      <PatientTestimonials procedureName="Breast" />
      <RelatedProceduresSection />
    </>
  );
}
